import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListDivider from '@mui/joy/ListDivider';
import Checkbox from '@mui/joy/Checkbox';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Textarea from '@mui/joy/Textarea';

import Error from 'components/Error';
import Success from 'components/Success';
import FancyIcon from 'components/FancyIcon';
import useCalendarAuth from 'hooks/useCalendarAuth';
import useUserIdentity from 'hooks/useUserIdentity';
import useAuth from 'hooks/useAuth';
import useWorkKey from 'hooks/useWorkKey';
import getIcon from 'utils/getIcon';
import moment, { MomentZoneOffset } from 'moment-timezone';

import { CalendarListItemType } from 'stores/calendar_auth_store';


import { CalendarFrame } from 'components/CalendarTools';
import useUser from 'hooks/useUser';
import { FormControl, FormLabel } from '@mui/joy';

const CalendarList = () => {
    const {
        fetchAvailableCalendars,
        addToSyncedCalendars,
        removeFromSyncedCalendars
    } = useCalendarAuth();

    const { currentIdentity } = useUserIdentity();
    const { currentUser } = useAuth();
    const [calendars, setCalendars] = useState([] as CalendarListItemType[]);
    const [fetching, setFetching] = useState(false);
    useEffect(() => {
        if(currentIdentity.uuid) {
            setFetching(true);
            fetchAvailableCalendars(currentIdentity.uuid)
                .then(data => {
                    console.log("Setting calendar: ");
                    console.log(data);
                    setCalendars(data);
                })
                .finally(() =>
                    setFetching(false)
                );
        }
    }, [fetchAvailableCalendars, currentIdentity.uuid]);

    function updateSyncedCalendar(cal: CalendarListItemType, checked: boolean) {
        if(checked) {
            addToSyncedCalendars(currentIdentity.uuid, cal.id)
                .then(() => {
                    Success(`Added ${cal.summary} to your synced calendars`)
                });
        }
        else {
            removeFromSyncedCalendars(currentIdentity.uuid, cal.id)
                .then(() => {
                    Success(`Removed ${cal.summary} from your synced calendars`)
                });
        }
    }

    if(fetching) {
        return <em>Loading Calendars</em>
    }

    return (
        <List>
            {
                (calendars.length > 0) && calendars.map(cal => {
                    return (
                        <ListItem key={cal.id}>
                            <Checkbox
                                label={cal.summary}
                                onChange={(e) => updateSyncedCalendar(cal, e.target.checked)}
                                defaultChecked={currentUser.synced_calendars.includes(cal.id)}
                            />
                        </ListItem>
                    );
                })
            }
        </List>
    );
};

function ZoneOption({zone}:{zone: MomentZoneOffset}) {
    const name = zone.name.replaceAll('_', ' ');
    return <Option value={zone.name}>{name} ({zone.offset > 0 ? '+' : ''}{zone.offset / 60})</Option>
}
const TimeZoneSettings = () => {
    const { currentUser, updateCurrentUser } = useAuth();
    const { updateField } = useUser();
    const [saving, setSaving] = useState(false);
    const [currentZone, setCurrentZone] = useState(currentUser.timezone_city);

    const LoadingIcon = getIcon('Replay');
    const Check = getIcon('Check');


    useEffect(() => {
        if (!currentUser?.uuid) {
            return;
        }

        if (!currentUser.timezone_city) {
            const t = moment.tz.guess();
            if (t) {
                handleTimeZoneChange(null, t);
                setCurrentZone(t);
            }
        }

    }, [currentUser?.uuid, currentUser?.timezone_city]);

    function handleTimeZoneChange(_: React.SyntheticEvent | null,
        zone: string | null,
    ) {
        if(!zone) return;
        if(saving) return;
        setCurrentZone(zone);
        setSaving(true);
        updateField('timezone_city', zone)
            .then((user) => {
                setSaving(false);
                updateCurrentUser(user);
                Success(`Timezone set to ${user.timezone_city}`);
            });

        //currentUser.setTimaeZone(e.target.value);
    }
    const s = (a: MomentZoneOffset, b: MomentZoneOffset) => {
        if(['America/New_York', 'America/Los_Angeles', 'America/Chicago', 'America/Denver'].includes(a.name)) return -1;
        if(a.offset > b.offset) return 1;
        if(a.offset < b.offset) return -1;
        return 0;
    }


    var zones = moment.tz.zonesForCountry('US', true);
    zones.filter(zone => !zone.name.startsWith('America/Indiana') &&
        !zone.name.startsWith('America/North_Dakota') &&
        !zone.name.startsWith('America/Kentucky'))
        .sort(s);
    var ca_zones = moment.tz.zonesForCountry('CA', true);
    ca_zones.sort(s);
    var uk_zones = moment.tz.zonesForCountry('GB', true);
    uk_zones.sort(s);

    console.log("timezone:", moment.tz.guess());

    return (
        <Stack>
            <Typography>Set your timezone</Typography>
            <Stack
                direction="row"
                spacing={1}
            >

                <Select
                    placeholder="Set your timezone"
                    value={currentZone}
                    onChange={handleTimeZoneChange}
                >


          <List
              aria-labelledby={'select-group-us'}
            sx={{ '--ListItemDecorator-size': '28px' }}
          >
            <ListItem id={`select-group-us`} sticky>
              <Typography level="body-xs" textTransform="uppercase">
                USA
              </Typography>
            </ListItem>

                    {
                        zones.map(zone => {
                            return <ZoneOption zone={zone} />;
                        })
                    }
              <ListDivider role="none" />

            <ListItem id={'select-group-ca'} sticky>
              <Typography level="body-xs" textTransform="uppercase">
                Canada
              </Typography>
            </ListItem>
                    {
                        ca_zones.map(zone => {
                            return <ZoneOption zone={zone} />;
                        })
                    }

              <ListDivider role="none" />

            <ListItem id={'select-group-uk'} sticky>
              <Typography level="body-xs" textTransform="uppercase">
                The UK
              </Typography>
            </ListItem>
                    {
                        uk_zones.map(zone => {
                            return <ZoneOption zone={zone} />;
                        })
                    }
            </List>
        </Select>

                {saving &&
                    <LoadingIcon
                        sx={{ animation: 'backspin 2s linear infinite' }}
                    />}
                {!saving && <Check />}
            </Stack>
        </Stack>
    );
};

const CalendarAuthSettings = observer(() => {
    const { currentUser } = useAuth();
    const { fetchCalendarPreferences, fetchAuthUrl, saveCalendarPreferences } = useCalendarAuth();

    const workKey = useWorkKey();
    const { currentIdentity } = useUserIdentity();
    const [calendarPreferences, setCalendarPreferences] = useState(currentUser.calendar_preferences);
    const [savedPreferences, setSavedPreferences] = useState(currentUser.calendar_preferences);

    useEffect(() => {
        if(workKey) {
            fetchCalendarPreferences(workKey)
                .then((data) => {
                    if(data) {
                        setCalendarPreferences(data);
                    }
                });
        }
    }, []);

    async function handleSignIn() {
        const data = await fetchAuthUrl(currentIdentity.uuid);
        if(data) {
            window.location.href = data;
        }
        else {
            Error("Unable to start sync calendar sync");
        }
    }

    function savePrefs() {
        saveCalendarPreferences(workKey, calendarPreferences)
            .then(() => {
                setCalendarPreferences(calendarPreferences);
                setSavedPreferences(calendarPreferences);
                Success("Saved Calendar Preferences");
            });
    }

    return (
        <Stack m={3} spacing={2}>
            <TimeZoneSettings />

            { !currentUser.has_calendar &&
                <Stack>
                    <Typography>Sync your Google calendar.</Typography>
                    <Button onClick={handleSignIn}>Sync Google Calendar</Button>
                </Stack>
            }

            { currentUser.has_calendar &&
                <Stack>
                    <Stack>
                        <Typography>
                            Select the calendars that we'll use.
                        </Typography>
                        { currentIdentity.role == 'client' &&
                        <Typography level="body-xs">
                            Your assistants will be able to see the selected calendars when scheduling appointments.
                        </Typography>
                        }
                    </Stack>
                    <CalendarList />

                    <Stack pt={1} spacing={1}>
                        <FormControl>
                            <FormLabel>Anything we should know when scheduling?</FormLabel>
                            <Textarea
                                placeholder="Example: No lunch meetings please..."
                                minRows={3}
                                value={calendarPreferences}
                                onChange={(e) => setCalendarPreferences(e.target.value)} />
                        </FormControl>

                            <Button
                                disabled={calendarPreferences === savedPreferences}
                                onClick={savePrefs}>Save Preferences</Button>

                    </Stack>
                </Stack>
            }

        </Stack>
    );
});


export const CalendarPreview = observer(() => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    return (
        <Stack>
            { currentUser.has_calendar &&
                <Stack>
                    <CalendarFrame
                        showChrome={false}
                        associated_type="current-user"
                        associated_uuid={currentUser.uuid}
                    />
                </Stack>
            }
            { !currentUser.has_calendar &&
                <Stack m={3}>
                    <Stack>
                        <Typography level="title-lg">
                            You haven't synced your calendar yet
                        </Typography>
                        <Typography>
                            Set your timezone and sync your calendar
                        </Typography>
                    </Stack>
                    <Stack direction="row" mt={2}>
                        <Button
                            startDecorator={<FancyIcon name='DayOnesCalendar' />}
                            onClick={() => {
                                navigate('/calendar/settings');
                            }}>
                            Set up your calendar
                        </Button>
                    </Stack>
                </Stack>
            }
        </Stack>
    );
});

export default CalendarAuthSettings;
